import React from 'react';
import { RiskMeterSliceDto } from '../../../../dtos/RiskMeterSliceDto';
import './RiskMeter.css';

interface SliceTooltipProps {
    slice: RiskMeterSliceDto;
}

// US date format MM/DD/YYYY
const formatDate = (d: Date | string | null | undefined): string => {
    if (!d) return '-';
    const date = typeof d === 'string' ? new Date(d) : d;
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

export default function SliceTooltip({ slice }: SliceTooltipProps) {
    if (slice.score === null) {
        return <div style={{ padding: '8px' }}><b>{slice.label}</b>: Not Started</div>;
    }

    if (slice.label === 'Buffer') {
        const cellStyle = { padding: '4px 8px', textAlign: 'center' as const };
        const headerStyle = { ...cellStyle, backgroundColor: '#f7f7f7' };
        const resultCellStyle = { ...cellStyle, textAlign: 'right' as const };
        const resultHeaderStyle = { ...headerStyle, textAlign: 'right' as const };

        return (
            <div style={{ padding: '8px', fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <b>Buffer</b>
                    <b>Score: {slice.score}</b>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '15px' }}>
                    <tbody>
                        <tr style={{ height: '28px' }}>
                            <th style={headerStyle}>Construction Completion</th>
                            <th style={headerStyle}>Milestones Completion</th>
                            <th style={resultHeaderStyle}>Result</th>
                        </tr>
                        <tr>
                            <td style={cellStyle}><i>{formatDate(slice.projectedEnd)} (Projected)</i></td>
                            <td style={cellStyle}>
                                {slice.usedNowForVariance
                                    ? <i>{formatDate(slice.projectedStart)} (Now)</i>
                                    : slice.usedProjectedForStart
                                        ? <i>{formatDate(slice.projectedStart)} (Projected)</i>
                                        : formatDate(slice.projectedStart)}
                            </td>
                            <td style={resultCellStyle}>{slice.actual?.toFixed(1)} mo</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }

    const projectedStart = formatDate(slice.projectedStart);
    const projectedEnd = formatDate(slice.projectedEnd);
    const actualStart = formatDate(slice.actualStart);
    const actualEnd = formatDate(slice.actualEnd);
    const nowDate = formatDate(new Date());

    const cellStyle = { padding: '4px 8px', textAlign: 'center' as const };
    const headerStyle = { ...cellStyle, backgroundColor: '#f7f7f7' };
    const rowLabelStyle = { ...cellStyle, fontWeight: 'bold' as const, textAlign: 'left' as const };
    const resultCellStyle = { ...cellStyle, textAlign: 'right' as const };
    const resultHeaderStyle = { ...headerStyle, textAlign: 'right' as const };

    const actualEndDisplay = slice.usedNowForVariance ? <i>{nowDate} (Now)</i> : actualEnd;

    return (
        <div style={{ padding: '8px', fontSize: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <b>{slice.label}</b>
                <b>Score: {slice.score}</b>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '15px' }}>
                <tbody>
                    <tr style={{ height: '28px' }}>
                        <th style={headerStyle}></th>
                        <th style={headerStyle}>Completion</th>
                        <th style={headerStyle}>Start</th>
                        <th style={resultHeaderStyle}>Result</th>
                    </tr>
                    <tr>
                        <td style={rowLabelStyle}>Projected</td>
                        <td style={cellStyle}>{projectedEnd}</td>
                        <td style={cellStyle}>{projectedStart}</td>
                        <td style={resultCellStyle}>{slice.projected.toFixed(1)} mo</td>
                    </tr>
                    <tr>
                        <td style={{ ...rowLabelStyle, paddingBottom: '19px' }}>Actual</td>
                        <td style={{ ...cellStyle, paddingBottom: '19px' }}>{actualEndDisplay}</td>
                        <td style={{ ...cellStyle, paddingBottom: '19px' }}>
                            {slice.usedProjectedForStart
                                ? <i>{actualStart} (Projected)</i>
                                : actualStart}
                        </td>
                        <td style={{ ...resultCellStyle, paddingBottom: '19px' }}>{slice.actual?.toFixed(1) ?? '-'} mo</td>
                    </tr>
                    <tr style={{ height: '28px' }}>
                        <th style={headerStyle}></th>
                        <th style={headerStyle}>Projected</th>
                        <th style={headerStyle}>Actual</th>
                        <th style={resultHeaderStyle}>Result</th>
                    </tr>
                    <tr>
                        <td style={rowLabelStyle}>Deviation</td>
                        <td style={cellStyle}>{projectedEnd}</td>
                        <td style={cellStyle}>{actualEndDisplay}</td>
                        <td style={resultCellStyle}>{slice.variance?.toFixed(1)} mo</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
